import { computed, ref, type ComputedRef, type Ref } from 'vue'

import type { ApprovalStage, WorkflowInstance } from '@/types/workflow'
import { getApprovalStageLabel, getHistoricalMaxStage } from '@/utils/workflowPresentation'
import { formatWorkflowRichText } from '@/utils/workflowRichText'

import {
  buildStoryboardEditorText,
  normalizeEditorContent,
} from './workspacePresentation'

function getConfirmedStageContent(workflow: WorkflowInstance | null, stage: ApprovalStage): string {
  const confirmed = workflow?.data?.input_data?.stage_confirmed_content as Record<string, unknown> | undefined
  return normalizeEditorContent(String(confirmed?.[stage] || ''))
}

const STAGE_ORDER: ApprovalStage[] = ['proposal', 'script', 'storyboard', 'final']

interface UpdateStageContentOptions {
  stage: string
  content: string
}

interface UseStageResultWorkspaceOptions {
  projectId: number
  workflow: Ref<WorkflowInstance | null>
  acting: Ref<boolean>
  currentStage: ComputedRef<ApprovalStage>
  primaryStageContent: ComputedRef<string>
  editorContent: Ref<string>
  editorDirty: Ref<boolean>
  canInspectPhase: (stage: ApprovalStage, status: string) => boolean
  stageStatus: (stage: ApprovalStage) => string
  updateStageContent: (projectId: number, options: UpdateStageContentOptions) => Promise<WorkflowInstance>
  getActionErrorMessage: (error: any) => string | null
  success: (message: string) => void
  error: (message: string) => void
}

export function useStageResultWorkspace(options: UseStageResultWorkspaceOptions) {
  const saving = ref(false)

  const canEditCurrentStage = computed(() => ['proposal', 'script', 'storyboard', 'final'].includes(options.currentStage.value))
  const showStageResultPanel = computed(() => !options.acting.value && (options.currentStage.value === 'final' || Boolean(options.primaryStageContent.value || options.editorContent.value)))
  const inspectableStageOptions = computed(() => {
    const currentIndex = STAGE_ORDER.indexOf(options.currentStage.value)
    const historicalIndex = STAGE_ORDER.indexOf(getHistoricalMaxStage(options.workflow.value))
    const maxVisibleIndex = Math.max(currentIndex, historicalIndex)

    return STAGE_ORDER
      .filter((stage, index) => index <= maxVisibleIndex || options.canInspectPhase(stage, options.stageStatus(stage)))
      .map((key) => ({ key, label: getApprovalStageLabel(key) }))
  })
  const currentInspectableStageIndex = computed(() => inspectableStageOptions.value.findIndex((item) => item.key === options.currentStage.value))
  const previousInspectableStage = computed(() => currentInspectableStageIndex.value > 0 ? inspectableStageOptions.value[currentInspectableStageIndex.value - 1].key : null)
  const nextInspectableStage = computed(() => currentInspectableStageIndex.value >= 0 && currentInspectableStageIndex.value < inspectableStageOptions.value.length - 1
    ? inspectableStageOptions.value[currentInspectableStageIndex.value + 1].key
    : null)
  const renderedEditorContent = computed(() => formatWorkflowRichText(options.editorContent.value || options.primaryStageContent.value))
  const hasUnsavedEdits = computed(() => options.editorDirty.value && Boolean(options.editorContent.value.trim()))
  const editorHint = computed(() => {
    const hints: Record<ApprovalStage, string> = {
      proposal: '可以直接调整方案正文，确认后将据此进入剧本生成。',
      script: '可以直接调整剧本内容，确认后将据此进入分镜生成。',
      storyboard: '可直接修改分镜文本；若贴入 JSON 数组，将同步覆盖 scenes 数据。',
      final: '可补充交付说明或最终确认摘要。',
    }
    return hints[options.currentStage.value]
  })

  function buildEditableStageContent(): string {
    const data = options.workflow.value?.data || {}
    if (options.currentStage.value === 'proposal') {
      return normalizeEditorContent(String(data.proposal_generation_result?.proposal || getConfirmedStageContent(options.workflow.value, 'proposal') || options.primaryStageContent.value || ''))
    }
    if (options.currentStage.value === 'script') {
      return normalizeEditorContent(String(data.script_generation_result?.script_content || getConfirmedStageContent(options.workflow.value, 'script') || options.primaryStageContent.value || ''))
    }
    if (options.currentStage.value === 'storyboard') {
      const storyboard = data.storyboard_generation_result || {}
      return buildStoryboardEditorText(storyboard)
        || getConfirmedStageContent(options.workflow.value, 'storyboard')
        || normalizeEditorContent(String(options.primaryStageContent.value || ''))
    }
    return normalizeEditorContent(String(data.final_packaging_result?.delivery_summary || getConfirmedStageContent(options.workflow.value, 'final') || options.primaryStageContent.value || ''))
  }

  function syncEditorFromWorkflow(force: boolean) {
    if (!force && options.editorDirty.value) {
      return
    }
    options.editorContent.value = buildEditableStageContent()
    options.editorDirty.value = false
  }

  function markEditorDirty() {
    options.editorDirty.value = true
  }

  function handleCancelEdits() {
    syncEditorFromWorkflow(true)
  }

  async function handleSaveEdits() {
    if (!canEditCurrentStage.value || !options.editorDirty.value || !options.editorContent.value.trim()) {
      return
    }

    saving.value = true
    try {
      options.workflow.value = await options.updateStageContent(options.projectId, {
        stage: options.currentStage.value,
        content: options.editorContent.value,
      })
      options.editorDirty.value = false
      options.success('当前阶段内容已保存')
    } catch (error: any) {
      options.error(options.getActionErrorMessage(error) || '保存修改失败')
    } finally {
      saving.value = false
    }
  }

  return {
    canEditCurrentStage,
    editorHint,
    handleCancelEdits,
    handleSaveEdits,
    hasUnsavedEdits,
    inspectableStageOptions,
    markEditorDirty,
    nextInspectableStage,
    previousInspectableStage,
    renderedEditorContent,
    saving,
    showStageResultPanel,
    syncEditorFromWorkflow,
  }
}